import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Package, Users, Truck, Building2, Plus, Pencil, Trash2,
  Search, X, Save, MapPin, Phone, Mail, CreditCard,
  FileText, ChevronLeft, ChevronRight, CalendarPlus, ClipboardList, ArrowRight, Copy, Check, Calendar, Clock, Paperclip
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Pagination, usePagination } from '../components/Pagination';
import MaterialDatabaseTab from '../components/MaterialDatabaseTab';
import BillOfQuantitiesTab from '../components/BillOfQuantitiesTab';
import { Database, ClipboardList as BoqIcon } from 'lucide-react';

const API_URL = '';

// Inline copy-to-clipboard button for PO Payment popup
function CopyBtn({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') return null;
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label || 'Copied'}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <button onClick={onCopy} className="ml-1.5 p-0.5 rounded hover:bg-muted/60 text-muted-foreground hover:text-primary transition-colors align-middle"
      title={`Copy ${label || ''}`} data-testid={`copy-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

export default function SupplyChainPage({ user, onLogout, theme, onThemeToggle }) {
  const isFullAccess = user?.role === 'principal' || user?.role === 'admin';

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-orange-500/10">
          <Package className="w-6 h-6 text-orange-500" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Supply Chain Hub</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Vendor management, procurement, and logistics</p>
        </div>
      </div>

      <Tabs scope="supply-chain" defaultValue="procurement" className="space-y-4">
        <TabsList className="bg-muted/50 w-full sm:w-auto flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="procurement" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-procurement">
            <Package className="w-3.5 h-3.5" /> Procurement
          </TabsTrigger>
          {isFullAccess && (
            <TabsTrigger value="logistics" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-logistics">
              <Truck className="w-3.5 h-3.5" /> Logistics
            </TabsTrigger>
          )}
          {isFullAccess && (
            <TabsTrigger value="vendors" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-vendors">
              <Building2 className="w-3.5 h-3.5" /> Vendors
            </TabsTrigger>
          )}
          <TabsTrigger value="materials" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-materials">
            <Database className="w-3.5 h-3.5" /> Material Database
          </TabsTrigger>
          <TabsTrigger value="boq" className="gap-1.5 text-xs sm:text-sm" data-testid="tab-boq">
            <BoqIcon className="w-3.5 h-3.5" /> Bill of Quantities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="procurement">
          <ProcurementHub user={user} />
        </TabsContent>

        {isFullAccess && (
          <TabsContent value="logistics">
            <LogisticsPanel user={user} />
          </TabsContent>
        )}

        {isFullAccess && (
          <TabsContent value="vendors">
            <VendorManagement user={user} />
          </TabsContent>
        )}

        <TabsContent value="materials">
          <MaterialDatabaseTab />
        </TabsContent>

        <TabsContent value="boq">
          <BillOfQuantitiesTab />
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
}

// ===== Vendor Management Component =====
function VendorManagement({ user }) {
  const [vendors, setVendors] = useState([]);
  const [cities, setCities] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterTrade, setFilterTrade] = useState('');

  const emptyForm = {
    company_name: '', trades: [], company_address: '', city: '', owners_name: '',
    contact_number: '', email: '', bank_name: '', account_name: '', account_number: '',
    branch: '', delivery_contact_person: '', delivery_contact_number: '', pickup_location: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [tradeInput, setTradeInput] = useState('');

  const loadVendors = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCity) params.set('city', filterCity);
      if (filterTrade) params.set('trade', filterTrade);
      const res = await axios.get(`${API_URL}/api/supply-chain/vendors?${params}`);
      setVendors(res.data);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  }, [filterCity, filterTrade]);

  const loadMeta = useCallback(async () => {
    try {
      const [citiesRes, tradesRes] = await Promise.all([
        axios.get(`${API_URL}/api/supply-chain/cities`),
        axios.get(`${API_URL}/api/supply-chain/vendors/trades`)
      ]);
      setCities(citiesRes.data.cities);
      setAllTrades(tradesRes.data);
    } catch {}
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const openNew = () => { setForm(emptyForm); setEditingVendor(null); setTradeInput(''); setShowForm(true); };
  const openEdit = (v) => { setForm({ ...v }); setEditingVendor(v); setTradeInput(''); setShowForm(true); };

  const addTrade = () => {
    const t = tradeInput.trim();
    if (t && !form.trades.includes(t)) setForm(f => ({ ...f, trades: [...f.trades, t] }));
    setTradeInput('');
  };
  const removeTrade = (t) => setForm(f => ({ ...f, trades: f.trades.filter(x => x !== t) }));

  const handleSave = async () => {
    if (!form.company_name.trim()) return toast.error('Company name is required');
    if (!form.city) return toast.error('City is required');
    try {
      if (editingVendor) {
        await axios.put(`${API_URL}/api/supply-chain/vendors/${editingVendor.id}`, form);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API_URL}/api/supply-chain/vendors`, form);
        toast.success('Vendor created');
      }
      setShowForm(false);
      loadVendors();
      loadMeta();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save vendor'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await axios.delete(`${API_URL}/api/supply-chain/vendors/${id}`); toast.success('Vendor deleted'); loadVendors(); loadMeta(); }
    catch { toast.error('Failed to delete'); }
  };

  // Filter, paginate, then re-group by city
  const filtered = useMemo(() => vendors.filter(v =>
    (!search || v.company_name.toLowerCase().includes(search.toLowerCase()) || v.trades.some(t => t.toLowerCase().includes(search.toLowerCase())))
  ), [vendors, search]);
  const vendorPage = usePagination(filtered, 20);
  const grouped = {};
  vendorPage.items.forEach(v => { const c = v.city || 'Uncategorized'; if (!grouped[c]) grouped[c] = []; grouped[c].push(v); });
  const sortedCities = Object.keys(grouped).sort();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
          <Building2 className="w-5 h-5 text-orange-500" /> Vendor Management
        </h2>
        <Button size="sm" onClick={openNew} data-testid="btn-add-vendor">
          <Plus className="w-4 h-4 mr-1" /> Add Vendor
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors or trades..." className="pl-8 h-9 text-sm" data-testid="vendor-search" />
        </div>
        <select value={filterCity} onChange={e => setFilterCity(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="filter-city">
          <option value="">All Cities</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterTrade} onChange={e => setFilterTrade(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="filter-trade">
          <option value="">All Trades</option>
          {allTrades.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Vendor List grouped by city */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading vendors...</div>
      ) : sortedCities.length === 0 ? (
        <Card className="glass-effect border-border/40">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No vendors found</p>
            <p className="text-xs mt-1">Add your first vendor to get started</p>
          </CardContent>
        </Card>
      ) : (
        sortedCities.map(city => (
          <div key={city} className="space-y-2">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 px-1">
              <MapPin className="w-3 h-3" /> {city} ({grouped[city].length})
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[city].map(vendor => (
                <Card key={vendor.id} className="glass-effect border-border/40 hover:border-primary/30 transition-all" data-testid={`vendor-card-${vendor.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground truncate">{vendor.company_name}</h4>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{vendor.owners_name}</p>
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0">
                        <button onClick={() => openEdit(vendor)} className="p-1 hover:bg-muted rounded" data-testid={`btn-edit-vendor-${vendor.id}`}><Pencil className="w-3 h-3 text-muted-foreground" /></button>
                        <button onClick={() => handleDelete(vendor.id)} className="p-1 hover:bg-destructive/10 rounded" data-testid={`btn-delete-vendor-${vendor.id}`}><Trash2 className="w-3 h-3 text-destructive/60" /></button>
                      </div>
                    </div>
                    {vendor.trades.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {vendor.trades.map(t => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 font-medium">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 space-y-0.5 text-[10px] text-muted-foreground">
                      {vendor.contact_number && <p className="flex items-center gap-1"><Phone className="w-2.5 h-2.5" /> {vendor.contact_number}</p>}
                      {vendor.email && <p className="flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> {vendor.email}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
      <Pagination page={vendorPage.page} totalPages={vendorPage.totalPages} setPage={vendorPage.setPage} total={vendorPage.total} pageSize={vendorPage.pageSize} label="vendors" testIdPrefix="vendor-pg" />

      {/* Vendor Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Supplier Accreditation Form'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Company Details */}
            <div>
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Company Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>Company Name *</Label>
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Company name" data-testid="vendor-company-name" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Trades</Label>
                  <div className="flex gap-1.5 mb-1.5">
                    <Input value={tradeInput} onChange={e => setTradeInput(e.target.value)} placeholder="Add trade..." className="flex-1"
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrade(); }}} data-testid="vendor-trade-input" />
                    <Button variant="outline" size="sm" onClick={addTrade} type="button"><Plus className="w-3.5 h-3.5" /></Button>
                  </div>
                  {form.trades.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {form.trades.map(t => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-600 flex items-center gap-1">
                          {t} <button onClick={() => removeTrade(t)} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label>Company Address</Label>
                  <Input value={form.company_address} onChange={e => setForm(f => ({ ...f, company_address: e.target.value }))} placeholder="Full address" />
                </div>
                <div>
                  <Label>City *</Label>
                  <select value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="vendor-city">
                    <option value="">Select City</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Owner's Name</Label>
                  <Input value={form.owners_name} onChange={e => setForm(f => ({ ...f, owners_name: e.target.value }))} placeholder="Owner name" />
                </div>
                <div>
                  <Label>Contact Number</Label>
                  <Input value={form.contact_number} onChange={e => setForm(f => ({ ...f, contact_number: e.target.value }))} placeholder="Phone" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" />
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div>
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Payment Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="Bank name" /></div>
                <div><Label>Account Name</Label><Input value={form.account_name} onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))} placeholder="Account name" /></div>
                <div><Label>Account Number</Label><Input value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Account number" /></div>
                <div><Label>Branch</Label><Input value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))} placeholder="Branch" /></div>
              </div>
            </div>

            {/* Delivery Details */}
            <div>
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Delivery Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Contact Person</Label><Input value={form.delivery_contact_person} onChange={e => setForm(f => ({ ...f, delivery_contact_person: e.target.value }))} placeholder="Contact person" /></div>
                <div><Label>Contact Number</Label><Input value={form.delivery_contact_number} onChange={e => setForm(f => ({ ...f, delivery_contact_number: e.target.value }))} placeholder="Phone" /></div>
                <div className="sm:col-span-2"><Label>Pickup Location</Label><Input value={form.pickup_location} onChange={e => setForm(f => ({ ...f, pickup_location: e.target.value }))} placeholder="Pickup location address" /></div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-border/50">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="btn-save-vendor">
                <Save className="w-4 h-4 mr-2" /> {editingVendor ? 'Update Vendor' : 'Save Vendor'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ===== Procurement Hub Component =====
const PO_STATUSES = ["Draft", "Pending Approval", "For Payment", "Paid for Delivery", "Delivered", "Fulfilled"];
const STATUS_COLORS = {
  "Draft": "bg-gray-100 text-gray-700 border-gray-300",
  "Pending Approval": "bg-yellow-50 text-yellow-700 border-yellow-300",
  "For Payment": "bg-blue-50 text-blue-700 border-blue-300",
  "Paid for Delivery": "bg-purple-50 text-purple-700 border-purple-300",
  "Delivered": "bg-green-50 text-green-700 border-green-300",
  "Fulfilled": "bg-emerald-50 text-emerald-800 border-emerald-400",
};

function ProcurementHub({ user }) {
  const isFullAccess = user?.role === 'principal' || user?.role === 'admin';
  const isPrincipal = user?.role === 'principal';
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectDirectory, setProjectDirectory] = useState([]);
  const [pos, setPOs] = useState([]);
  const [view, setView] = useState('board'); // 'board' | 'project'
  const [activeProject, setActiveProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [loading, setLoading] = useState(true);

  const emptyForm = { project_name: '', project_code: '', vendor_id: '', vendor_name: '', amount: 0, items: '', delivery_date: '', status: 'Draft', notes: '', quotation_url: null, sales_invoice_url: null, payment_slip_url: null };
  const [form, setForm] = useState(emptyForm);

  const [allPOs, setAllPOs] = useState([]);
  const [summaryFilter, setSummaryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // PO Summary — filtered + paginated (20 per page)
  const filteredPOs = useMemo(() => allPOs
    .filter(po => !summaryFilter || po.project_name === summaryFilter)
    .filter(po => !statusFilter || po.status === statusFilter),
    [allPOs, summaryFilter, statusFilter]);
  const poSummaryPage = usePagination(filteredPOs, 20);
  const [paymentVendor, setPaymentVendor] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [vRes, pRes, dirRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/supply-chain/vendors`),
        axios.get(`${API_URL}/api/supply-chain/po/projects`),
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/supply-chain/po`),
      ]);
      setVendors(vRes.data);
      setProjects(pRes.data);
      setProjectDirectory(dirRes.data);
      setAllPOs(allRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  const [pettyCash, setPettyCash] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);

  const loadProjectPOs = useCallback(async (projName) => {
    try {
      const res = await axios.get(`${API_URL}/api/supply-chain/po?project_name=${encodeURIComponent(projName)}`);
      setPOs(res.data);
      // Also load petty cash entries tagged to this project
      const pcRes = await axios.get(`${API_URL}/api/petty-cash/by-project?project_name=${encodeURIComponent(projName)}`).catch(() => ({ data: [] }));
      setPettyCash(pcRes.data || []);
      // And reimbursements tagged to this project
      const rbRes = await axios.get(`${API_URL}/api/reimbursements/by-project?project_name=${encodeURIComponent(projName)}`).catch(() => ({ data: [] }));
      setReimbursements(rbRes.data || []);
    } catch { toast.error('Failed to load POs'); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openBoard = (proj) => {
    setActiveProject(proj);
    setView('project');
    loadProjectPOs(proj.project_name);
  };

  const openNewPO = (projName = '') => {
    setForm({ ...emptyForm, project_name: projName || activeProject?.project_name || '' });
    setEditingPO(null);
    setShowForm(true);
  };

  const openEditPO = async (po) => {
    // Fetch the freshest PO so attachments uploaded in a prior session/dialog are always shown
    // (the cached board list may be stale after an upload).
    let fresh = po;
    try {
      const res = await axios.get(`${API_URL}/api/supply-chain/po/${po.id}`);
      if (res.data) fresh = res.data;
    } catch { /* fall back to list copy */ }
    setForm({ project_name: fresh.project_name, project_code: fresh.project_code, vendor_id: fresh.vendor_id, vendor_name: fresh.vendor_name, amount: fresh.amount, items: fresh.items, delivery_date: fresh.delivery_date || '', status: fresh.status, notes: fresh.notes || '', quotation_url: fresh.quotation_url || null, sales_invoice_url: fresh.sales_invoice_url || null, payment_slip_url: fresh.payment_slip_url || null });
    setEditingPO(fresh);
    setShowForm(true);
  };

  const showPaymentDetails = (po) => {
    const v = vendors.find(x => x.id === po.vendor_id);
    if (v) setPaymentVendor({ po, vendor: v });
    else { toast.error('Vendor details not found'); openEditPO(po); }
  };

  const copyPaymentDetails = async (po) => {
    const v = vendors.find(x => x.id === po.vendor_id) || {};
    const block = [
      `PO Number: ${po.control_number || '-'}`,
      `Project Name: ${po.project_name || '-'}`,
      `Amount: ${po.amount ? `₱${po.amount.toLocaleString()}` : '-'}`,
      `Details: ${po.items || '-'}`,
      `Account Name: ${v.account_name || '-'}`,
      `Account Number: ${v.account_number || '-'}`,
      `Bank Branch: ${v.branch || '-'}`,
      `Email: ${v.email || '-'}`,
      `Business Address: ${v.company_address || '-'}`,
    ].join('\n');
    try { await navigator.clipboard.writeText(block); toast.success('Payment details copied'); }
    catch { toast.error('Copy failed'); }
  };

  const handleSavePO = async () => {
    if (!form.project_name.trim()) return toast.error('Project name required');
    try {
      if (editingPO) {
        await axios.put(`${API_URL}/api/supply-chain/po/${editingPO.id}`, form);
        toast.success('PO updated');
      } else {
        await axios.post(`${API_URL}/api/supply-chain/po`, form);
        toast.success('PO created');
      }
      setShowForm(false);
      loadData();
      if (activeProject) loadProjectPOs(activeProject.project_name);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save PO'); }
  };

  const updateStatus = async (poId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/supply-chain/po/${poId}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      if (activeProject) loadProjectPOs(activeProject.project_name);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const deletePO = async (poId) => {
    if (!window.confirm('Delete this PO?')) return;
    try { await axios.delete(`${API_URL}/api/supply-chain/po/${poId}`); toast.success('PO deleted'); loadData(); if (activeProject) loadProjectPOs(activeProject.project_name); }
    catch { toast.error('Failed to delete'); }
  };

  // Project Board View (Kanban)
  if (view === 'project' && activeProject) {
    const grouped = {};
    PO_STATUSES.forEach(s => { grouped[s] = []; });
    pos.forEach(po => { if (grouped[po.status]) grouped[po.status].push(po); });

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setView('board')} data-testid="btn-back-board"><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-base sm:text-lg font-bold text-foreground">{activeProject.project_name}</h2>
            <span className="text-xs text-muted-foreground">({activeProject.total} POs)</span>
          </div>
          <Button size="sm" onClick={() => openNewPO()} data-testid="btn-new-po-project"><Plus className="w-4 h-4 mr-1" /> New PO</Button>
        </div>

        {/* Kanban columns */}
        <div className="flex gap-3 overflow-x-auto pb-4">
          {PO_STATUSES.map(status => (
            <div key={status} className="flex-shrink-0 w-[220px]">
              <div className={`text-xs font-bold uppercase tracking-wider px-2 py-1.5 rounded-t-md border ${STATUS_COLORS[status]}`}>
                {status} ({grouped[status].length})
                {status === 'For Payment' && (
                  <div className="text-[11px] font-extrabold normal-case mt-0.5" data-testid="for-payment-total">
                    Total: ₱{grouped[status].reduce((s, po) => s + (po.amount || 0), 0).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="border border-t-0 border-border/40 rounded-b-md bg-muted/10 min-h-[100px] space-y-1.5 p-1.5">
                {grouped[status].map(po => (
                  <div key={po.id} className="bg-card border border-border/40 rounded p-2 text-xs space-y-1 hover:border-primary/30 cursor-pointer"
                    onClick={() => status === 'For Payment' ? showPaymentDetails(po) : openEditPO(po)} data-testid={`po-card-${po.id}`}>
                    <div className="font-semibold text-foreground">{po.control_number}</div>
                    <div className="text-muted-foreground truncate">{po.vendor_name || 'No vendor'}</div>
                    <div className="flex justify-between">
                      <span className="font-medium">{po.amount ? `₱${po.amount.toLocaleString()}` : '-'}</span>
                      {po.delivery_date && <span className="text-muted-foreground">{po.delivery_date.slice(5)}</span>}
                    </div>
                    {/* Status transition buttons */}
                    <div className="flex gap-1 pt-1 border-t border-border/30">
                      {status === 'Draft' && <button onClick={e => { e.stopPropagation(); updateStatus(po.id, 'Pending Approval'); }} className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200">Submit</button>}
                      {status === 'Pending Approval' && isPrincipal && <button onClick={e => { e.stopPropagation(); updateStatus(po.id, 'For Payment'); }} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Approve</button>}
                      {status === 'For Payment' && <button onClick={e => { e.stopPropagation(); updateStatus(po.id, 'Paid for Delivery'); }} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200">Mark Paid</button>}
                      {status === 'For Payment' && <button onClick={e => { e.stopPropagation(); copyPaymentDetails(po); }} title="Copy payment details" data-testid={`copy-payment-details-${po.id}`} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 inline-flex items-center gap-0.5"><Copy className="w-2.5 h-2.5" /> Copy</button>}
                      {status === 'Paid for Delivery' && <button onClick={e => { e.stopPropagation(); updateStatus(po.id, 'Delivered'); }} className="text-[9px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200">Delivered</button>}
                      {status === 'Delivered' && <button onClick={e => { e.stopPropagation(); updateStatus(po.id, 'Fulfilled'); }} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200">Fulfill</button>}
                      {isFullAccess && <button onClick={e => { e.stopPropagation(); deletePO(po.id); }} className="text-[9px] px-1.5 py-0.5 rounded text-destructive hover:bg-destructive/10 ml-auto"><Trash2 className="w-2.5 h-2.5" /></button>}
                    </div>
                  </div>
                ))}
                {/* Petty Cash entries (purple) — render only in Fulfilled column */}
                {status === 'Fulfilled' && pettyCash.map(pc => (
                  <div key={`pc-${pc.id}`} className="bg-purple-50 dark:bg-purple-950/40 border border-purple-300 dark:border-purple-700 rounded p-2 text-xs space-y-1" data-testid={`petty-cash-tile-${pc.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-purple-700 dark:text-purple-300">{pc.control_number || `PC-${pc.id.slice(0,6).toUpperCase()}`}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-200 text-purple-800 font-semibold">Petty Cash</span>
                    </div>
                    <div className="text-purple-900/80 dark:text-purple-200/80 truncate">{pc.item}</div>
                    <div className="flex justify-between text-purple-800 dark:text-purple-300">
                      <span className="font-medium">₱{(pc.expenses || 0).toLocaleString()}</span>
                      <span className="text-purple-700/70 dark:text-purple-400/70">{pc.date?.slice(5)}</span>
                    </div>
                    <div className="text-[10px] text-purple-700/70 dark:text-purple-400/70 truncate">by {pc.submitter_name || 'Unknown'}</div>
                    {pc.receipt_url && (
                      <a href={`${API_URL}${pc.receipt_url}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                        <Paperclip className="w-2.5 h-2.5" /> Receipt
                      </a>
                    )}
                  </div>
                ))}
                {/* Reimbursement entries (green) — render only in Fulfilled column */}
                {status === 'Fulfilled' && reimbursements.map(rb => (
                  <div key={`rb-${rb.id}`} className="bg-green-50 dark:bg-green-950/40 border border-green-300 dark:border-green-700 rounded p-2 text-xs space-y-1" data-testid={`reimbursement-tile-${rb.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-green-700 dark:text-green-300">RB-{rb.id.slice(0,6).toUpperCase()}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-200 text-green-800 font-semibold">Reimbursement</span>
                    </div>
                    <div className="text-green-900/80 dark:text-green-200/80 truncate">{rb.description}</div>
                    <div className="flex justify-between text-green-800 dark:text-green-300">
                      <span className="font-medium">₱{(rb.amount || 0).toLocaleString()}</span>
                      <span className="text-green-700/70 dark:text-green-400/70">{(rb.date || rb.created_at || '').slice(5,10)}</span>
                    </div>
                    <div className="text-[10px] text-green-700/70 dark:text-green-400/70 truncate">by {rb.user_name || 'Unknown'} · {rb.status}</div>
                    {rb.receipt_url && (
                      <a href={`${API_URL}${rb.receipt_url}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                        <Paperclip className="w-2.5 h-2.5" /> Receipt
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Payment Details Popup */}
        {paymentVendor && (
          <Dialog open={!!paymentVendor} onOpenChange={() => setPaymentVendor(null)}>
            <DialogContent className="bg-card border-border max-w-2xl">
              <DialogHeader><DialogTitle>Payment Reference — {paymentVendor.po.control_number}</DialogTitle></DialogHeader>
              <div className="flex gap-6 py-2">
                {/* PO Details — Left */}
                <div className="flex-1 space-y-3">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Purchase Order</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Control #</span><span className="font-medium">{paymentVendor.po.control_number}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Project</span><span>{paymentVendor.po.project_name}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Vendor</span><span>{paymentVendor.po.vendor_name}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-bold text-foreground">₱{paymentVendor.po.amount?.toLocaleString()}</span></div>
                    {paymentVendor.po.items && <div><span className="text-muted-foreground text-xs">Items:</span><p className="text-xs mt-0.5 whitespace-pre-line text-muted-foreground">{paymentVendor.po.items}</p></div>}
                    {paymentVendor.po.delivery_date && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{paymentVendor.po.delivery_date}</span></div>}
                    {(paymentVendor.po.quotation_url || paymentVendor.po.sales_invoice_url || paymentVendor.po.payment_slip_url) && (
                      <div className="pt-1.5 border-t border-border/30 space-y-1">
                        <div className="text-muted-foreground text-[10px] uppercase tracking-wider">Attachments</div>
                        {paymentVendor.po.quotation_url && (
                          <a href={`${API_URL}${paymentVendor.po.quotation_url}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="payment-popup-quotation">
                            <FileText className="w-3 h-3" /> Quotation
                          </a>
                        )}
                        {paymentVendor.po.sales_invoice_url && (
                          <a href={`${API_URL}${paymentVendor.po.sales_invoice_url}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="payment-popup-sales-invoice">
                            <FileText className="w-3 h-3" /> Sales Invoice
                          </a>
                        )}
                        {paymentVendor.po.payment_slip_url && (
                          <a href={`${API_URL}${paymentVendor.po.payment_slip_url}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="payment-popup-payment-slip">
                            <FileText className="w-3 h-3" /> Payment Slip
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Vendor Payment Details — Right */}
                <div className="flex-1 space-y-3 border-l border-border/40 pl-6">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Vendor Payment Details</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Bank</span><span className="font-medium flex items-center">{paymentVendor.vendor.bank_name || '-'}<CopyBtn value={paymentVendor.vendor.bank_name} label="Bank" /></span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Account Name</span><span className="flex items-center">{paymentVendor.vendor.account_name || '-'}<CopyBtn value={paymentVendor.vendor.account_name} label="Account Name" /></span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Account #</span><span className="font-mono font-medium flex items-center">{paymentVendor.vendor.account_number || '-'}<CopyBtn value={paymentVendor.vendor.account_number} label="Account #" /></span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Branch</span><span className="flex items-center">{paymentVendor.vendor.branch || '-'}<CopyBtn value={paymentVendor.vendor.branch} label="Branch" /></span></div>
                  </div>
                  <div className="pt-2 border-t border-border/30 space-y-1.5 text-sm">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Contact</h4>
                    <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="text-primary flex items-center">{paymentVendor.vendor.email || '-'}<CopyBtn value={paymentVendor.vendor.email} label="Email" /></span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="flex items-center">{paymentVendor.vendor.contact_number || '-'}<CopyBtn value={paymentVendor.vendor.contact_number} label="Phone" /></span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="flex items-center">{paymentVendor.vendor.owners_name || '-'}<CopyBtn value={paymentVendor.vendor.owners_name} label="Owner" /></span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground shrink-0">Address</span><span className="flex items-center text-right">{paymentVendor.vendor.company_address || '-'}<CopyBtn value={paymentVendor.vendor.company_address} label="Address" /></span></div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-border/50">
                <Button variant="outline" size="sm" data-testid="btn-copy-all-payment"
                  onClick={async () => {
                    const v = paymentVendor.vendor;
                    const p = paymentVendor.po;
                    const block = [
                      `Ref: ${p.control_number}`,
                      `Project: ${p.project_name}`,
                      `Vendor: ${p.vendor_name}`,
                      `Amount: ₱${p.amount?.toLocaleString()}`,
                      `Bank: ${v.bank_name || '-'}`,
                      `Account Name: ${v.account_name || '-'}`,
                      `Account #: ${v.account_number || '-'}`,
                      `Branch: ${v.branch || '-'}`,
                    ].join('\n');
                    try { await navigator.clipboard.writeText(block); toast.success('All payment info copied'); }
                    catch { toast.error('Copy failed'); }
                  }}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy All Payment Info
                </Button>
                <Button variant="outline" size="sm" data-testid="btn-copy-payment-details" onClick={() => copyPaymentDetails(paymentVendor.po)}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy Payment Details
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setPaymentVendor(null); openEditPO(paymentVendor.po); }}>Edit PO</Button>
                <Button size="sm" onClick={async () => { await updateStatus(paymentVendor.po.id, 'Paid for Delivery'); setPaymentVendor(null); }} data-testid="btn-mark-paid">Mark as Paid</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* PO Form Dialog */}
        <POFormDialog show={showForm} onClose={() => setShowForm(false)} form={form} setForm={setForm} vendors={vendors} editing={editingPO} onSave={handleSavePO} projectDirectory={projectDirectory} />
      </div>
    );
  }

  // PO Control Board — Project Tiles
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-orange-500" /> PO Control Board
        </h2>
        <Button size="sm" onClick={() => openNewPO()} data-testid="btn-new-po"><Plus className="w-4 h-4 mr-1" /> New Purchase Order</Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>
      ) : projects.length === 0 ? (
        <Card className="glass-effect border-border/40"><CardContent className="py-12 text-center text-muted-foreground">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No purchase orders yet</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map(proj => (
            <Card key={proj.project_name} className="glass-effect border-border/40 hover:border-primary/30 cursor-pointer transition-all hover-lift"
              onClick={() => openBoard(proj)} data-testid={`project-tile-${proj.project_name}`}>
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-foreground mb-1">{proj.project_name}</h4>
                <p className="text-xs text-muted-foreground mb-2">{proj.total} POs · ₱{proj.total_amount?.toLocaleString()}</p>
                <div className="flex flex-wrap gap-1.5">
                  {proj.pending_approval > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Approval: {proj.pending_approval}</span>}
                  {proj.for_payment > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Payment: {proj.for_payment}</span>}
                  {proj.paid_for_delivery > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">Delivery: {proj.paid_for_delivery}</span>}
                  {proj.draft > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Draft: {proj.draft}</span>}
                  {proj.fulfilled > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Done: {proj.fulfilled}</span>}
                </div>
                <div className="flex items-center justify-end mt-2 text-[10px] text-primary"><span>View Board</span> <ArrowRight className="w-3 h-3 ml-0.5" /></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* PO Summary — List View */}
      {allPOs.length > 0 && (() => {
        const totalAmount = filteredPOs.reduce((sum, po) => sum + (po.amount || 0), 0);

        return (
        <div className="space-y-3 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" /> P.O. Summary
            </h3>
            <div className="flex gap-2">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="po-status-filter">
                <option value="">All Status</option>
                {PO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={summaryFilter} onChange={e => setSummaryFilter(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="po-summary-filter">
                <option value="">All Projects</option>
                {[...new Set(allPOs.map(p => p.project_name))].sort().map(pn => (
                  <option key={pn} value={pn}>{pn}</option>
                ))}
              </select>
            </div>
          </div>
          <Card className="glass-effect border-border/40 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border/40 bg-muted/30">
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Control #</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Project</th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Vendor</th>
                    <th className="text-right px-3 py-2 font-semibold text-foreground">
                      <div>Amount</div>
                      <div className="text-[10px] font-bold text-primary">Total: ₱{totalAmount.toLocaleString()}</div>
                    </th>
                    <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Items</th>
                    <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Delivery</th>
                    <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Status</th>
                    <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {poSummaryPage.items.map(po => (
                    <tr key={po.id} className="border-b border-border/20 hover:bg-muted/10" data-testid={`po-summary-row-${po.id}`}>
                      <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{po.control_number}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{po.project_name}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{po.vendor_name || '-'}</td>
                      <td className="px-3 py-2 text-right text-foreground font-medium whitespace-nowrap">{po.amount ? `₱${po.amount.toLocaleString()}` : '-'}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate">{po.items?.split('\n')[0] || '-'}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground whitespace-nowrap">{po.delivery_date || '-'}</td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[po.status]}`}>{po.status}</span>
                      </td>
                      <td className="px-3 py-2 text-center text-muted-foreground whitespace-nowrap">{po.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 pb-2">
              <Pagination page={poSummaryPage.page} totalPages={poSummaryPage.totalPages} setPage={poSummaryPage.setPage} total={poSummaryPage.total} pageSize={poSummaryPage.pageSize} label="POs" testIdPrefix="po-summary-pg" />
            </div>
          </Card>
        </div>
        );
      })()}

      <POFormDialog show={showForm} onClose={() => setShowForm(false)} form={form} setForm={setForm} vendors={vendors} editing={editingPO} onSave={handleSavePO} projectDirectory={projectDirectory} />
    </div>
  );
}

// PO Form Dialog (shared)
function POAttachmentRow({ poId, label, docType, url, onChange }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/supply-chain/po/${poId}/upload-doc?doc_type=${docType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${label}`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/supply-chain/po/${poId}/doc?doc_type=${docType}`);
      onChange(null);
      toast.success(`${label} removed`);
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <div className="flex items-center gap-2 p-2 rounded bg-muted/20 border border-border/30">
      <span className="text-xs font-medium text-foreground min-w-[100px]">{label}</span>
      {url ? (
        <>
          <a href={`${API_URL}${url}`} target="_blank" rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1 flex-1 truncate"
            data-testid={`po-doc-link-${docType}`}>
            <FileText className="w-3 h-3" />
            View {label}
          </a>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="text-[10px] px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20"
            data-testid={`po-doc-replace-${docType}`}>Replace</button>
          <button type="button" onClick={handleRemove}
            className="text-[10px] px-2 py-1 rounded text-destructive hover:bg-destructive/10"
            data-testid={`po-doc-remove-${docType}`}>Remove</button>
        </>
      ) : (
        <>
          <span className="flex-1 text-xs text-muted-foreground italic">No file attached</span>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="text-xs px-3 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 font-medium disabled:opacity-50"
            data-testid={`po-doc-upload-${docType}`}>
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </>
      )}
      <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

function POFormDialog({ show, onClose, form, setForm, vendors, editing, onSave, projectDirectory = [] }) {
  if (!show) return null;

  const onProjectSelect = (e) => {
    const projId = e.target.value;
    if (!projId) { setForm(f => ({ ...f, project_name: '', project_code: '' })); return; }
    const proj = projectDirectory.find(p => p.id === projId);
    if (proj) setForm(f => ({ ...f, project_name: proj.project_name, project_code: proj.project_code || '' }));
  };

  const selectedProjId = projectDirectory.find(p => p.project_name === form.project_name)?.id || '';

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? `Edit PO: ${editing.control_number}` : 'New Purchase Order'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Project *</Label>
            <select value={selectedProjId} onChange={onProjectSelect}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="po-project">
              <option value="">Select project</option>
              {projectDirectory.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </div>
          <div>
            <Label>Project Code</Label>
            <Input value={form.project_code} readOnly className="bg-muted/30 cursor-not-allowed" data-testid="po-project-code" />
          </div>
          <div><Label>Vendor</Label>
            <select value={form.vendor_id || (form.vendor_name === 'Draft' ? '__draft__' : '')} onChange={e => {
              const val = e.target.value;
              if (val === '__draft__') { setForm(f => ({ ...f, vendor_id: '', vendor_name: 'Draft' })); return; }
              const v = vendors.find(x => x.id === val);
              setForm(f => ({ ...f, vendor_id: val, vendor_name: v?.company_name || '' }));
            }}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="po-vendor">
              <option value="">Select vendor</option>
              <option value="__draft__">Draft (vendor TBD)</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.company_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (₱)</Label><Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} placeholder="0" data-testid="po-amount" /></div>
            <div><Label>Delivery Date</Label><Input type="date" value={form.delivery_date} onChange={e => setForm(f => ({ ...f, delivery_date: e.target.value }))} data-testid="po-delivery-date" /></div>
          </div>
          <div><Label>Items</Label>
            <Textarea value={form.items} onChange={e => setForm(f => ({ ...f, items: e.target.value }))} placeholder="List items..." className="min-h-[80px]" data-testid="po-items" />
          </div>
          <div><Label>Status</Label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="po-status">
              {PO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" /></div>

          {/* Document Attachments — available only after PO exists */}
          {editing ? (
            <div className="space-y-2 pt-2 border-t border-border/40">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attachments</Label>
              <POAttachmentRow poId={editing.id} label="Quotation" docType="quotation" url={form.quotation_url}
                onChange={(newUrl) => setForm(f => ({ ...f, quotation_url: newUrl }))} />
              <POAttachmentRow poId={editing.id} label="Sales Invoice" docType="sales_invoice" url={form.sales_invoice_url}
                onChange={(newUrl) => setForm(f => ({ ...f, sales_invoice_url: newUrl }))} />
              <POAttachmentRow poId={editing.id} label="Payment Slip" docType="payment_slip" url={form.payment_slip_url}
                onChange={(newUrl) => setForm(f => ({ ...f, payment_slip_url: newUrl }))} />
            </div>
          ) : (
            <p className="text-[10px] text-muted-foreground italic">Save the PO first, then re-open it to attach a Quotation, Sales Invoice or Payment Slip.</p>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-border/50">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={onSave} data-testid="btn-save-po"><Save className="w-4 h-4 mr-2" /> {editing ? 'Update' : 'Create PO'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Logistics Component =====
function LogisticsPanel({ user }) {
  const [deliveries, setDeliveries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [dateValue, setDateValue] = useState('');
  const [timeValue, setTimeValue] = useState('08:00');
  const [expandedId, setExpandedId] = useState(null);
  const dateInputRef = React.useRef(null);
  const timeInputRef = React.useRef(null);

  const openPicker = (ref) => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el.focus();
    el.click();
  };

  const loadData = useCallback(async () => {
    try {
      const [delRes, venRes] = await Promise.all([
        axios.get(`${API_URL}/api/supply-chain/logistics/deliveries`),
        axios.get(`${API_URL}/api/supply-chain/vendors`),
      ]);
      setDeliveries(delRes.data);
      setVendors(venRes.data);
    } catch { toast.error('Failed to load deliveries'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getVendor = (po) => vendors.find(v => v.id === po.vendor_id) || vendors.find(v => v.company_name === po.vendor_name);

  const updateDate = async (poId) => {
    try {
      await axios.patch(`${API_URL}/api/supply-chain/po/${poId}/delivery-date?delivery_date=${dateValue}`);
      toast.success('Delivery date updated');
      setEditingDate(null);
      loadData();
    } catch { toast.error('Failed to update date'); }
  };

  const addToCalendar = async (po) => {
    try {
      await axios.post(`${API_URL}/api/calendar/tasks`, {
        task_name: `Delivery: ${po.control_number} — ${po.vendor_name}`,
        task_date: po.delivery_date,
        task_time: po._calTime || '08:00',
        priority: 'High',
        department: 'Construction',
        description: `PO: ${po.control_number}\nVendor: ${po.vendor_name}\nItems: ${po.items}\nAmount: ₱${po.amount?.toLocaleString()}`
      });
      toast.success('Added to Construction Calendar');
    } catch { toast.error('Failed to add to calendar'); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
        <Truck className="w-5 h-5 text-orange-500" /> Delivery Tracking
      </h2>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>
      ) : deliveries.length === 0 ? (
        <Card className="glass-effect border-border/40"><CardContent className="py-12 text-center text-muted-foreground">
          <Truck className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No deliveries scheduled</p>
          <p className="text-xs mt-1">POs marked "Paid for Delivery" will appear here</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {deliveries.map(po => {
            const vendor = getVendor(po);
            const isExpanded = expandedId === po.id;
            return (
              <Card key={po.id} className="glass-effect border-border/40" data-testid={`delivery-card-${po.id}`}>
                <CardContent className="p-3 space-y-0">
                  {/* Main row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : po.id)}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{po.control_number}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${STATUS_COLORS[po.status]}`}>{po.status}</span>
                        <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{po.vendor_name} · {po.project_name}</p>
                      {po.items && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{po.items.split('\n')[0]}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {editingDate === po.id ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <div className="relative">
                            <Input ref={dateInputRef} type="date" value={dateValue} onChange={e => setDateValue(e.target.value)} className="h-8 text-xs w-36 pr-7" />
                            <button type="button" onClick={() => openPicker(dateInputRef)} className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted/50 text-primary" data-testid="btn-pick-date">
                              <Calendar className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="relative">
                            <Input ref={timeInputRef} type="time" value={timeValue} onChange={e => setTimeValue(e.target.value)} className="h-8 text-xs w-28 pr-7" />
                            <button type="button" onClick={() => openPicker(timeInputRef)} className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted/50 text-primary" data-testid="btn-pick-time">
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <Button size="sm" className="h-8 text-xs" onClick={() => updateDate(po.id)}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditingDate(null)}>X</Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-medium text-foreground">{po.delivery_date}</span>
                          <span className="text-xs text-muted-foreground">{po._calTime || '08:00'}</span>
                          <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={() => { setEditingDate(po.id); setDateValue(po.delivery_date || ''); setTimeValue(po._calTime || '08:00'); }} data-testid={`btn-edit-date-${po.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => { po._calTime = timeValue; addToCalendar(po); }} data-testid={`btn-add-cal-${po.id}`}>
                        <CalendarPlus className="w-3 h-3 mr-0.5" /> Calendar
                      </Button>
                    </div>
                  </div>

                  {/* Expanded: Delivery Details + Contact Info */}
                  {isExpanded && vendor && (
                    <div className="mt-3 pt-3 border-t border-border/40 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Delivery Details */}
                      <div className="space-y-1.5">
                        <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Truck className="w-3 h-3" /> Delivery Details</h4>
                        <div className="text-xs space-y-1 bg-muted/20 rounded-md p-2.5">
                          <div className="flex justify-between"><span className="text-muted-foreground">Contact Person</span><span className="font-medium text-foreground">{vendor.delivery_contact_person || '-'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Contact Number</span><span className="font-medium text-foreground">{vendor.delivery_contact_number || '-'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Pickup Location</span><span className="font-medium text-foreground">{vendor.pickup_location || '-'}</span></div>
                        </div>
                      </div>
                      {/* Company Contact Info */}
                      <div className="space-y-1.5">
                        <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Building2 className="w-3 h-3" /> Vendor Contact</h4>
                        <div className="text-xs space-y-1 bg-muted/20 rounded-md p-2.5">
                          <div className="flex justify-between"><span className="text-muted-foreground">Company</span><span className="font-medium text-foreground">{vendor.company_name}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="font-medium text-foreground">{vendor.owners_name || '-'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium text-foreground">{vendor.contact_number || '-'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="text-primary font-medium">{vendor.email || '-'}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium text-foreground text-right max-w-[180px]">{vendor.company_address || '-'}</span></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {isExpanded && !vendor && (
                    <div className="mt-3 pt-3 border-t border-border/40">
                      <p className="text-xs text-muted-foreground">No vendor details available for this PO.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
